/**
 * LeaderboardPage
 *
 * Public Strides leaderboard with three scopes: Global / Monthly / City.
 * Reads `GET /api/leaderboard?scope=...` and (when signed in)
 * `GET /api/leaderboard/me?scope=...` to show the user's own rank.
 *
 * Design intent: gamified but not shouty. Podium for top 3, dense list for
 * the rest. The signed-in user's own row is highlighted and "Share my rank"
 * gives them a native-share card to brag.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Trophy, Globe, Calendar, MapPin, Share2, ArrowLeft, Crown, Medal, Award, Flame } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCOPES = [
  { key: 'global', label: 'Global', icon: Globe },
  { key: 'monthly', label: 'This Month', icon: Calendar },
  { key: 'streak', label: 'Streaks', icon: Flame },
  { key: 'city', label: 'My City', icon: MapPin },
];

const rankBadge = (rank) => {
  if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" strokeWidth={2.5} />;
  if (rank === 2) return <Medal className="w-5 h-5 text-slate-400" strokeWidth={2.5} />;
  if (rank === 3) return <Award className="w-5 h-5 text-orange-500" strokeWidth={2.5} />;
  return null;
};

const initials = (name) => (name || '?').split(' ').map((s) => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const currentMonthLabel = () =>
  new Date().toLocaleString('en-US', { month: 'long', year: 'numeric' });

const LeaderboardPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialScope = SCOPES.find((s) => s.key === searchParams.get('scope'))?.key || 'global';
  const [scope, setScope] = useState(initialScope);
  const [city, setCity] = useState(searchParams.get('city') || user?.city || '');
  const [cityInput, setCityInput] = useState(city);
  const [entries, setEntries] = useState([]);
  const [myRank, setMyRank] = useState(null);
  const [loading, setLoading] = useState(true);

  const canQueryCity = useMemo(() => scope !== 'city' || Boolean(city && city.trim()), [scope, city]);

  const fetchData = useCallback(async () => {
    if (!canQueryCity) {
      setEntries([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { scope, limit: 100 };
      if (scope === 'city') params.city = city.trim();
      const publicRes = await axios.get(`${API}/leaderboard`, { params });
      setEntries(publicRes.data.entries || []);

      if (token) {
        try {
          const meRes = await axios.get(`${API}/leaderboard/me`, {
            params,
            headers: { Authorization: `Bearer ${token}` },
          });
          setMyRank(meRes.data);
        } catch {
          setMyRank(null);
        }
      } else {
        setMyRank(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  }, [scope, city, canQueryCity, token]);

  useEffect(() => {
    fetchData();
    const newParams = { scope };
    if (scope === 'city' && city) newParams.city = city;
    setSearchParams(newParams, { replace: true });
  }, [fetchData, scope, city, setSearchParams]);

  const handleShareMyRank = async () => {
    if (!myRank) return;
    const scopeLabel =
      scope === 'monthly' ? currentMonthLabel()
      : scope === 'city' ? `${city}`
      : scope === 'streak' ? 'Audiostride Streaks'
      : 'Audiostride Global';
    const value =
      scope === 'monthly' ? `${(myRank.monthly_total || 0).toLocaleString()} Strides this month`
      : scope === 'streak' ? `a ${myRank.current_streak || 0}-day streak 🔥`
      : `${(myRank.strides_balance || 0).toLocaleString()} Strides`;
    const text = `I'm #${myRank.rank} on the ${scopeLabel} leaderboard — ${value}. Join me on Audiostride!`;
    const url = `${window.location.origin}/leaderboard?scope=${scope}${scope === 'city' && city ? `&city=${encodeURIComponent(city)}` : ''}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'My Audiostride Rank', text, url });
      } else {
        await navigator.clipboard.writeText(`${text} ${url}`);
        toast.success('Copied to clipboard!');
      }
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Could not share');
    }
  };

  const podium = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="leaderboard-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <button
            onClick={() => navigate(-1)}
            data-testid="leaderboard-back-btn"
            className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm mb-6"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Trophy className="w-8 h-8 text-yellow-400" />
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">Strides Leaderboard</h1>
              </div>
              <p className="text-slate-300 text-base max-w-xl">
                Compete with fellow walkers. Earn Strides by exploring tours, sharing, and completing walks.
              </p>
            </div>
            {myRank && (
              <div className="bg-slate-800/70 backdrop-blur border border-slate-700 rounded-xl px-5 py-4 min-w-[220px]" data-testid="my-rank-card">
                <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">Your Rank</div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-black">{myRank.rank ? `#${myRank.rank}` : '—'}</span>
                  {myRank.is_private && <span className="text-xs text-slate-400">(private)</span>}
                </div>
                <div className="text-sm text-slate-300 mt-1 flex items-center gap-1">
                  {scope === 'monthly' && `${(myRank.monthly_total || 0).toLocaleString()} this month`}
                  {scope === 'streak' && (
                    <>
                      <Flame className="w-3.5 h-3.5 text-orange-400" />
                      {myRank.current_streak || 0}-day streak
                      {myRank.longest_streak > (myRank.current_streak || 0) && (
                        <span className="text-slate-400 text-xs">(best: {myRank.longest_streak})</span>
                      )}
                    </>
                  )}
                  {(scope === 'global' || scope === 'city') && `${(myRank.strides_balance || 0).toLocaleString()} Strides`}
                </div>
                <button
                  onClick={handleShareMyRank}
                  data-testid="share-my-rank-btn"
                  className="mt-3 inline-flex items-center gap-2 text-xs font-bold bg-white text-slate-900 rounded-lg px-3 py-1.5 hover:bg-slate-100"
                >
                  <Share2 className="w-3.5 h-3.5" /> Share my rank
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Scope tabs */}
      <div className="max-w-5xl mx-auto px-6 pt-8">
        <div className="flex flex-wrap gap-2 mb-6" data-testid="scope-tabs">
          {SCOPES.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setScope(key)}
              data-testid={`scope-${key}`}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                scope === key
                  ? 'bg-slate-900 text-white shadow-lg'
                  : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-100'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {/* City input */}
        {scope === 'city' && (
          <div className="flex gap-2 mb-6" data-testid="city-input-row">
            <input
              type="text"
              placeholder="Enter a city (e.g., Tel Aviv)"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && setCity(cityInput.trim())}
              data-testid="city-input"
              className="flex-1 max-w-sm px-4 py-2 rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
            />
            <button
              onClick={() => setCity(cityInput.trim())}
              data-testid="city-search-btn"
              className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800"
            >
              Search
            </button>
          </div>
        )}

        {/* Body */}
        {loading ? (
          <div className="text-center py-16 text-slate-500" data-testid="leaderboard-loading">
            Loading rankings...
          </div>
        ) : !canQueryCity ? (
          <div className="text-center py-16 text-slate-500">Enter a city to see rankings.</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-slate-500" data-testid="leaderboard-empty">
            No rankings yet {scope === 'city' ? `for ${city}` : ''}. Be the first!
          </div>
        ) : (
          <>
            {/* Podium */}
            {podium.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8" data-testid="podium">
                {[podium[1], podium[0], podium[2]].filter(Boolean).map((p) => (
                  <div
                    key={p.user_id}
                    data-testid={`podium-rank-${p.rank}`}
                    onClick={() => navigate(`/passport/user/${p.user_id}`)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/passport/user/${p.user_id}`); }}
                    title={`View ${p.display_name}'s passport`}
                    className={`rounded-2xl p-6 text-center border-2 cursor-pointer hover:shadow-lg transition-all ${
                      p.rank === 1
                        ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-400 order-first sm:order-none sm:-translate-y-2'
                        : p.rank === 2
                        ? 'bg-gradient-to-br from-slate-50 to-slate-100 border-slate-300'
                        : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-300'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1 mb-2">
                      {rankBadge(p.rank)}
                      <span className="text-2xl font-black text-slate-900">#{p.rank}</span>
                    </div>
                    <div
                      className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center font-black text-xl mb-3 border-4 ${
                        p.rank === 1 ? 'bg-yellow-100 text-yellow-900 border-yellow-300' : 'bg-white text-slate-900 border-slate-200'
                      }`}
                    >
                      {p.avatar_url ? (
                        <img src={p.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                      ) : (
                        initials(p.display_name)
                      )}
                    </div>
                    <div className="font-black text-slate-900 truncate">{p.display_name}</div>
                    {p.city && <div className="text-xs text-slate-500 truncate">{p.city}</div>}
                    <div className="mt-2 text-xl font-black text-slate-900 flex items-center justify-center gap-1">
                      {scope === 'streak' && <Flame className="w-5 h-5 text-orange-500" />}
                      {scope === 'streak'
                        ? p.current_streak
                        : (scope === 'monthly' ? p.monthly_total : p.strides_balance).toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 uppercase tracking-wide">
                      {scope === 'streak' ? `Day streak · Best ${p.longest_streak}` : 'Strides'}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Rest of list */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-testid="leaderboard-list">
              {rest.map((entry) => {
                const isMe = user && entry.user_id === user.user_id;
                return (
                  <div
                    key={entry.user_id}
                    data-testid={`leaderboard-row-${entry.rank}`}
                    onClick={() => navigate(`/passport/user/${entry.user_id}`)}
                    role="link"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/passport/user/${entry.user_id}`); }}
                    title={`View ${entry.display_name}'s passport`}
                    className={`flex items-center gap-4 px-5 py-4 border-b border-slate-100 last:border-b-0 cursor-pointer ${
                      isMe ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="w-10 text-center font-black text-slate-600">#{entry.rank}</div>
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center font-bold text-sm text-slate-700 overflow-hidden">
                      {entry.avatar_url ? (
                        <img src={entry.avatar_url} alt="" className="w-full h-full object-cover" />
                      ) : (
                        initials(entry.display_name)
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">
                        {entry.display_name} {isMe && <span className="text-blue-600 text-xs font-black ml-1">YOU</span>}
                      </div>
                      {(entry.city || entry.country) && (
                        <div className="text-xs text-slate-500 truncate">
                          {[entry.city, entry.country].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="font-black text-slate-900 flex items-center justify-end gap-1">
                        {scope === 'streak' && <Flame className="w-4 h-4 text-orange-500" />}
                        {scope === 'streak'
                          ? entry.current_streak
                          : (scope === 'monthly' ? entry.monthly_total : entry.strides_balance).toLocaleString()}
                      </div>
                      <div className="text-xs text-slate-500 uppercase tracking-wide">
                        {scope === 'streak' ? `Days · Best ${entry.longest_streak}` : 'Strides'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        <p className="text-center text-xs text-slate-400 mt-8 mb-12">
          {scope === 'monthly' && `Showing rankings for ${currentMonthLabel()}`}
          {scope === 'streak' && 'Showing active streaks (must have completed a tour today or yesterday)'}
          {(scope === 'global' || scope === 'city') && 'Showing all-time rankings'}
          {' · '}Rankings update in real-time
          {' · '}<button onClick={() => navigate('/profile')} className="underline hover:text-slate-600">Manage your visibility →</button>
        </p>
      </div>
    </div>
  );
};

export default LeaderboardPage;
